import React, { useMemo } from 'react';
import { Globe2 } from 'lucide-react';
import type { SiteTemplate } from '../schemas/validation';
import {
  normalizeComponents,
  processLayoutComponents,
  getTemplateThemeInfo,
} from '../utils/templateUtils';
import { ThemeProvider } from '../contexts/ThemeContext';
import { CustomColorsProvider } from '../contexts/CustomColorsContext';
import { ComponentRenderer } from './ComponentRenderer';

interface TemplateMiniPreviewProps {
  template: SiteTemplate;
}

/**
 * Renders a scaled-down snapshot of a template's first page components.
 * Used as a visual thumbnail in the site template library grid cards.
 * Wraps components with ThemeProvider and CustomColorsProvider so theme
 * colors and brand package fonts are applied correctly.
 * Falls back to a gradient + icon placeholder when the template has no components.
 */
export const TemplateMiniPreview: React.FC<TemplateMiniPreviewProps> = React.memo(
  ({ template }) => {
    const { pageComponents, headerComponents, footerComponents, hasContent } = useMemo(() => {
      const siteData = template.siteData as any;
      const pages = siteData?.pages || [];
      const layouts = siteData?.layouts || [];

      // Use the first page (home page)
      const firstPage = pages[0];
      const parsedPageComponents = normalizeComponents(firstPage?.components).sort(
        (a: any, b: any) => (a.order || 0) - (b.order || 0)
      );

      // Find the layout for this page
      let pageLayout = null;
      if (firstPage?.showLayout !== false) {
        if (firstPage?.layoutId) {
          pageLayout = layouts.find((l: any) => l.id === firstPage.layoutId);
        }
        if (!pageLayout) {
          pageLayout = layouts.find((l: any) => l.isDefault);
        }
      }

      const layoutComponents = pageLayout ? normalizeComponents(pageLayout.components) : [];
      const { headerComponents: header, footerComponents: footer } =
        processLayoutComponents(layoutComponents);

      const hasAnyContent =
        parsedPageComponents.length > 0 || header.length > 0 || footer.length > 0;

      return {
        pageComponents: parsedPageComponents,
        headerComponents: header,
        footerComponents: footer,
        hasContent: hasAnyContent,
      };
    }, [template]);

    const themeInfo = useMemo(() => getTemplateThemeInfo(template), [template]);

    // Fallback: gradient + icon when there are no components to render
    if (!hasContent) {
      return (
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-purple-200 flex items-center justify-center">
          <Globe2 className="h-12 w-12 text-gray-400" />
        </div>
      );
    }

    return (
      <div className="absolute inset-0 bg-white overflow-hidden">
        <ThemeProvider theme={themeInfo.themeName}>
          <CustomColorsProvider customColors={themeInfo.customColors} fonts={themeInfo.fonts}>
            <div
              className="pointer-events-none select-none origin-top-left"
              style={{
                transform: 'scale(0.22)',
                width: `${100 / 0.22}%`,
              }}
            >
              {/* Layout Header Components (e.g. navbar) */}
              {headerComponents
                .filter((c: any) => c.visible !== false)
                .map((component: any) => (
                  <ComponentRenderer key={component.id} component={component} isEditing={false} />
                ))}

              {/* Page Components */}
              {pageComponents
                .filter((c: any) => c.visible !== false)
                .map((component: any) => (
                  <ComponentRenderer key={component.id} component={component} isEditing={false} />
                ))}

              {/* Layout Footer Components */}
              {footerComponents
                .filter((c: any) => c.visible !== false)
                .map((component: any) => (
                  <ComponentRenderer key={component.id} component={component} isEditing={false} />
                ))}
            </div>
          </CustomColorsProvider>
        </ThemeProvider>
      </div>
    );
  }
);

TemplateMiniPreview.displayName = 'TemplateMiniPreview';
